import { generateId } from '@vedalib/editor/lib/utils/id'

import { getWindowData } from 'services/ScormData/ScormData'

import { getLaunchDomain } from '../middleware/lms.utils'
import type { LmsType, ILrsConfig, IXapiActor, ILmsUrlParams } from '../types'

export const FEATURE_FLAGS = {
  viewedElement: false,
  viewedBlock: true,
  testMode: false,
  isLocalStore: false,
  isPublicStore: false,
}

export const PUBLIC_VERB_ENDPOINT = 'http://adlnet.gov/expapi/'
export const LOCAL_BASE_IRI = 'https://parta.io/xapi/'

export interface ILmsSessionConfig {
  type: LmsType

  // Common
  uuid: string
  courseId: string
  exportId: string
  activityId: string

  // xAPI/cmi5 LRS config (from URL params or settings)
  masterLms: ILrsConfig

  analytics: {
    configUrl: string | null
  }

  // Feature flags
  featureFlags: typeof FEATURE_FLAGS

  // Legacy fields for backward compatibility
  verbEndpoint: string
  fetchUrl: string | null
}

const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return {
    endpoint: urlParams.get('endpoint'),
    actor: urlParams.get('actor'),
    activityId: urlParams.get('activity_id') || urlParams.get('activityId'),
    registration: urlParams.get('registration'),
    fetch: urlParams.get('fetch'),
    auth: urlParams.get('auth'),
  }
}

const parseActor = (actorParam: string | null): IXapiActor | null => {
  if (!actorParam) {
    return null
  }
  try {
    const parsed = JSON.parse(actorParam)
    return normalizeActor(parsed)
  } catch {
    return null
  }
}

export const normalizeActor = (parsedParamsActor: {
  name: string[] | string
  account?:
    | { accountServiceHomePage: string; accountName: string }[]
    | { accountServiceHomePage: string; accountName: string }
  mbox?: string
}): IXapiActor => {
  const name = Array.isArray(parsedParamsActor.name)
    ? parsedParamsActor.name.join(' ')
    : parsedParamsActor.name

  let mbox = parsedParamsActor.mbox || ''

  if (parsedParamsActor.account) {
    const account = Array.isArray(parsedParamsActor.account)
      ? parsedParamsActor.account[0]
      : parsedParamsActor.account
    if (account?.accountName) {
      mbox = `mailto:${account.accountName.replace(/^.*?\|/, '')}`
    }
  }

  return {
    mbox,
    name,
    objectType: 'Agent',
  }
}

export const DEFAULT_ACTOR: IXapiActor = {
  mbox: 'mailto:anonymous@example.com',
  name: 'Anonymous',
  objectType: 'Agent',
}

const ANONYMOUS_ID_KEY = 'XAPI_ANONYMOUS_ID'

export const getOrCreateAnonymousId = () => {
  try {
    const existing = localStorage.getItem(ANONYMOUS_ID_KEY)
    if (existing) {
      return existing
    }
    const id = generateId()
    localStorage.setItem(ANONYMOUS_ID_KEY, id)
    return id
  } catch {
    return generateId()
  }
}

export const buildAnonymousActor = (): IXapiActor => {
  const id = getOrCreateAnonymousId()
  const domain = getLaunchDomain()
  return {
    mbox: `mailto:session-${id}@${domain}`,
    name: 'Anonymous',
    objectType: 'Agent',
  }
}

export const getSessionId = (fetchUrl: string | null): string | undefined => {
  // Check URL params first (csSessionId from ContentSpace)
  const urlParams = new URLSearchParams(window.location.search)
  const csSessionId = urlParams.get('csSessionId')
  if (csSessionId) {
    return csSessionId
  }

  // Fallback to fetchUrl session param
  if (fetchUrl) {
    const match = fetchUrl.match(/[?&]session=([^&]+)/)
    if (match) {
      return match[1]
    }
  }
  return undefined
}

let _config: ILmsSessionConfig | null = null

export const getLmsUrlParams = (): ILmsUrlParams | null => {
  const urlParams = getUrlParams()
  const parsedActor = parseActor(urlParams.actor)
  const windowData = getWindowData()
  const exportId = windowData?.settings?.id || ''
  const activityId = urlParams.activityId || `urn:uuid:${exportId}`
  if (urlParams.endpoint && urlParams.auth && parsedActor) {
    return {
      endpoint: urlParams.endpoint,
      auth: urlParams.auth,
      actor: parsedActor,
      registration: urlParams.registration || undefined,
      fetch: urlParams.fetch || undefined,
      activityId,
    }
  }
  return null
}

export interface IAnalyticsConfigOverride {
  endpoint?: string
  auth?: string
  companyId?: string
  projectId?: string
  artifactId?: string
  exportDate?: string
  learnerId?: string
  learnerName?: string
  learnerEmail?: string
  lmsType?: string
  lmsVersion?: string
  sessionId?: string
}

export interface IAnalyticsConfig {
  fetchUrl: string
  override: IAnalyticsConfigOverride
}

export const getAnalyticsConfig = (): IAnalyticsConfig | null => {
  const windowData = getWindowData()
  return windowData?.analyticsConfig || null
}

export const buildLmsConfig = (): ILmsSessionConfig => {
  if (_config) {
    return _config
  }

  const windowData = getWindowData()
  const urlParams = getUrlParams()

  const uuid = generateId()
  const projectId = windowData?.data?.projectId || ''
  const exportId = windowData?.settings?.id || ''
  const activityId = urlParams.activityId || `urn:uuid:${exportId || uuid}`

  const parsedActor = parseActor(urlParams.actor)
  const actor = parsedActor || buildAnonymousActor()

  _config = {
    type: 'none',

    uuid,
    courseId: projectId,
    exportId,
    activityId,

    masterLms: {
      endpoint: urlParams.endpoint || '',
      auth: urlParams.auth || '',
      activityId,
      actor,
      registration: urlParams.registration || undefined,
    },

    analytics: {
      configUrl: windowData?.analyticsConfig?.fetchUrl || null,
    },

    featureFlags: { ...FEATURE_FLAGS },

    verbEndpoint: PUBLIC_VERB_ENDPOINT,
    fetchUrl: urlParams.fetch,
  }

  return _config
}

export const getLmsConfig = (): ILmsSessionConfig => {
  return _config || buildLmsConfig()
}

export const resetLmsConfig = (): void => {
  _config = null
}
